import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Hammer, Wrench, Home, Shield, Phone, Mail, MapPin, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Handwerker = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [
    {
      icon: Hammer,
      title: "Renovierungsarbeiten",
      description: "Komplette Renovierung von Räumen, Böden, Wänden und Decken mit höchster Qualität.",
      features: ["Malerarbeiten", "Bodenverlegung", "Trockenbau"]
    },
    {
      icon: Wrench,
      title: "Reparatur & Instandhaltung",
      description: "Schnelle und zuverlässige Reparaturen für Haus und Wohnung - vom kleinen bis zum großen Problem.",
      features: ["Sanitär", "Elektrik", "Tischlerarbeiten"]
    },
    {
      icon: Home,
      title: "Möbelmontage",
      description: "Professionelle Montage von Möbeln, Küchen und Einbauschränken nach Ihren Wünschen.",
      features: ["Küchenmontage", "Schränke", "Regalsysteme"]
    },
    {
      icon: Shield,
      title: "Modernisierung",
      description: "Modernisierung von Bad, Küche und Wohnräumen mit modernen Materialien und Techniken.",
      features: ["Badumbau", "Küchenumbau", "Smart Home"]
    }
  ];

  const features = [
    "Über 15 Jahre Erfahrung",
    "Faire und transparente Preise",
    "Pünktlichkeit & Zuverlässigkeit",
    "Qualitätsgarantie",
    "Kostenloser Kostenvoranschlag",
    "Notdienst verfügbar"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-50">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/#portfolio" className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors">
              <ArrowLeft size={20} />
              <span className="font-medium">Zurück zum Portfolio</span>
            </Link>
            <Button className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl">
              Anfrage senden
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-sm font-medium">
                <Hammer size={16} />
                <span>Ihr zuverlässiger Handwerker</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight">
                Handwerk mit Herz und Verstand
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Von der kleinen Reparatur bis zur großen Renovierung - wir sind Ihr Partner für alle handwerklichen Arbeiten rund um Haus und Wohnung in Kelsterbach und Umgebung.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl shadow-lg">
                  Kostenlos anfragen
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-blue-300 text-slate-700 hover:bg-blue-50 rounded-xl">
                  Notdienst: 0170 1234567
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1615974679600-665fb9468c4f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxoYW5keW1hbiUyMGNhcnBlbnRlcnxlbnwwfHx8fDE3NzM1Nzk2Nzh8MA&ixlib=rb-4.1.0&q=85"
                alt="Handwerker bei der Arbeit"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Unsere Leistungen
            </h2>
            <div className="w-20 h-1 bg-blue-500 mx-auto mb-6 rounded-full"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Professionelle Handwerksleistungen für Privat- und Geschäftskunden
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-blue-100 hover:shadow-xl transition-all rounded-2xl">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="text-blue-600" size={28} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-800 mb-2">{service.title}</h3>
                        <p className="text-slate-600 mb-4">{service.description}</p>
                        <div className="space-y-2">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <CheckCircle className="text-blue-500" size={16} />
                              <span className="text-slate-700 text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Unsere Arbeit
            </h2>
            <div className="w-20 h-1 bg-blue-500 mx-auto rounded-full"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <img
              src="https://images.unsplash.com/photo-1615974679548-2fcfd4699f62?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxoYW5keW1hbiUyMGNhcnBlbnRlcnxlbnwwfHx8fDE3NzM1Nzk2Nzh8MA&ixlib=rb-4.1.0&q=85"
              alt="Handwerker Projekt 1"
              className="rounded-2xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
            <img
              src="https://images.unsplash.com/photo-1667922578520-61558e79aa7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxoYW5keW1hbiUyMGNhcnBlbnRlcnxlbnwwfHx8fDE3NzM1Nzk2Nzh8MA&ixlib=rb-4.1.0&q=85"
              alt="Handwerker Projekt 2"
              className="rounded-2xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
            <img
              src="https://images.unsplash.com/photo-1581783898377-1c85bf937427?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxoYW5keW1hbiUyMGNhcnBlbnRlcnxlbnwwfHx8fDE3NzM1Nzk2Nzh8MA&ixlib=rb-4.1.0&q=85"
              alt="Handwerker Werkzeug"
              className="rounded-2xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
                Warum uns wählen?
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="text-white" size={14} />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <Card className="border-blue-100 rounded-2xl shadow-xl">
              <CardContent className="pt-6 space-y-6">
                <h3 className="text-2xl font-bold text-slate-800">Erreichbarkeit</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Clock className="text-blue-600" size={20} />
                    <div>
                      <div className="font-semibold text-slate-800">Mo - Fr</div>
                      <div className="text-slate-600">7:00 - 18:00 Uhr</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="text-blue-600" size={20} />
                    <div>
                      <div className="font-semibold text-slate-800">Samstag</div>
                      <div className="text-slate-600">8:00 - 14:00 Uhr</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="text-blue-600" size={20} />
                    <div>
                      <div className="font-semibold text-slate-800">Notdienst</div>
                      <div className="text-slate-600">24/7 erreichbar</div>
                    </div>
                  </div>
                </div>
                <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-xl">
                  Kostenlos anfragen
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
            Kontaktieren Sie uns
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Schildern Sie uns Ihr Anliegen - wir erstellen Ihnen gerne ein unverbindliches Angebot
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="border-blue-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <Phone className="text-blue-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">Telefon</div>
                <div className="text-slate-600">+49 (0) 6107 345678</div>
              </CardContent>
            </Card>
            <Card className="border-blue-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <Mail className="text-blue-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">E-Mail</div>
                <div className="text-slate-600">info@handwerk-meister.de</div>
              </CardContent>
            </Card>
            <Card className="border-blue-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <MapPin className="text-blue-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">Adresse</div>
                <div className="text-slate-600">Werkstraße 12, Kelsterbach</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-300">
            © 2025 Handwerk Meister • Erstellt von{' '}
            <Link to="/#portfolio" className="text-blue-400 hover:text-blue-300">
              WebWerk Kelsterbach
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Handwerker;
